// Modal zum Hinzufügen von Produkten (Content) zu einem Album. Ein Album-Eintrag = ein ganzes
// Produkt (nicht eine Variante wie bei Playlists) → keine Varianten-Expansion, nur Produkt-Suche.
import React, { useEffect, useState, useCallback } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, PhotoIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { audioService } from '../../services/audioService';
import { AudioContent } from '../../types/audio';

// Das Cover einer AUDIO-Zeile steht ausschließlich in `metadata.coverImage` — an der laufenden
// dev-API gemessen (siehe types/audio.ts). Die früheren Fallbacks auf `a.coverImage.url`,
// `a.coverImageUrl` und `a.contentData.coverImage` gab es dort nie; sie waren tote Zweige.
const coverOf = (a: AudioContent): string => a.metadata?.coverImage || '';

interface ProductPickerProps {
  existingContentIds: Set<string>;
  onAdd: (contentId: string) => Promise<void>;
  onClose: () => void;
}

export const ProductPicker: React.FC<ProductPickerProps> = ({ existingContentIds, onAdd, onClose }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<AudioContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await audioService.getAudioContent({ page: 1, limit: 25, search: q || undefined });
      setResults(res.data || []);
    } catch {
      setResults([]);
      setError('Produkte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const handleAdd = async (contentId: string) => {
    setBusyId(contentId);
    try {
      await onAdd(contentId);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="mt-16 w-full max-w-2xl bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-secondary-900">Produkt hinzufügen</h3>
          <button type="button" onClick={onClose} className="text-secondary-400 hover:text-secondary-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5">
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full"
              placeholder="Produkt suchen (Titel)…"
            />
          </div>

          <div className="max-h-[55vh] overflow-y-auto divide-y divide-secondary-100">
            {loading ? (
              <div className="py-10 text-center"><div className="loading-spinner mx-auto w-6 h-6"></div></div>
            ) : error ? (
              <p className="py-10 text-center text-error-600">{error}</p>
            ) : results.length === 0 ? (
              <p className="py-10 text-center text-secondary-500">Keine Produkte gefunden</p>
            ) : (
              results.map((content) => {
                const cover = coverOf(content);
                const already = existingContentIds.has(content.id);
                return (
                  <div key={content.id} className="flex items-center gap-3 py-3">
                    {cover ? (
                      <img src={cover} alt="" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                        <PhotoIcon className="w-5 h-5 text-secondary-400" />
                      </div>
                    )}
                    <span className="flex-1 text-sm font-medium text-secondary-900">{content.title}</span>
                    {already ? (
                      <span className="inline-flex items-center text-xs text-success-600">
                        <CheckIcon className="w-4 h-4 mr-1" /> hinzugefügt
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleAdd(content.id)}
                        disabled={busyId === content.id}
                        className="inline-flex items-center rounded border border-primary-500 px-2.5 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        {busyId === content.id ? '…' : 'Hinzufügen'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
